// @ts-check

/**
 * @file Provides a React Native text input wired to Api Maker forms.
 */

import {useForm} from "@kaspernj/api-maker/build/form"
import Text from "@kaspernj/api-maker/build/utils/text"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import {memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native"

import TextInput from "../text-input"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} FormTextInputProps
 * @property {string} [defaultValue] Initial input value.
 * @property {boolean} [disabled] Whether the field should reject user edits and keyboard submits.
 * @property {import("react-native").KeyboardTypeOptions} [keyboardType] Native keyboard layout.
 * @property {string} label Field label.
 * @property {boolean} [multiline] Whether the input should allow multiple lines.
 * @property {string} name Form field name.
 * @property {string} [placeholder] Placeholder text.
 * @property {boolean} [secureTextEntry] Whether text entry should be hidden.
 * @property {boolean} [submitOnEnter] Whether Enter submits single-line inputs and Ctrl/Cmd+Enter submits multiline inputs.
 * @property {string} testID Stable test id.
 */

/** @typedef {Record<string, never>} FormTextInputState */

/**
 * Reusable form text input with label and form integration.
 * @augments {ShapeComponent<FormTextInputProps, FormTextInputState>}
 */
class FormTextInput extends ShapeComponent {
  /** @type {ReturnType<typeof useForm> | null} */
  form = null
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  static propTypes = propTypesExact({
    defaultValue: PropTypes.string,
    disabled: PropTypes.bool,
    keyboardType: PropTypes.string,
    label: PropTypes.string.isRequired,
    multiline: PropTypes.bool,
    name: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    secureTextEntry: PropTypes.bool,
    submitOnEnter: PropTypes.bool,
    testID: PropTypes.string.isRequired
  })
  static defaultProps = {
    defaultValue: undefined,
    disabled: false,
    keyboardType: undefined,
    multiline: undefined,
    placeholder: undefined,
    secureTextEntry: undefined,
    submitOnEnter: true
  }

  /** Registers the field with the surrounding form. */
  setup() {
    this.form = useForm()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    const defaultValue = this.p.defaultValue
    const fieldName = this.p.name

    useEffect(() => {
      if (this.form && defaultValue !== undefined) {
        this.form.setValue(fieldName, defaultValue)
      }
    }, [defaultValue, fieldName])
  }

  /**
   * Renders the labeled text input.
   * @returns {ReactNode} Form text input content.
   */
  render() {
    const {disabled, label, multiline, placeholder, secureTextEntry, submitOnEnter, testID} = this.p
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <View>
        <Text
          style={styles[`label-${themeName}`] ||= {
            color: colors.textMuted,
            fontWeight: 600
          }}
          testID={`${testID}/label`}
        >
          {label}
        </Text>
        <TextInput
          defaultValue={this.props.defaultValue}
          editable={!disabled}
          keyboardType={this.props.keyboardType}
          multiline={multiline}
          numberOfLines={multiline ? 3 : undefined}
          onChangeText={this.tt.onChangeText}
          onEndEditing={this.tt.onEndEditing}
          onSubmitEditing={!disabled && submitOnEnter ? this.tt.onSubmitEditing : undefined}
          placeholder={placeholder}
          secureTextEntry={secureTextEntry}
          style={styles[`textInput-${themeName}-${Boolean(multiline)}`] ||= {
            borderRadius: 6,
            fontSize: 16,
            marginTop: 6
          }}
          testID={testID}
        />
      </View>
    )
  }

  /**
   * Writes a text value into the form.
   * @param {string} value Text value to store.
   * @returns {void} Nothing.
   */
  setFormValue(value) {
    if (this.form) this.form.setValue(this.props.name, value)
  }

  /**
   * Handles text changes from React Native TextInput.
   * @param {string} value Text value to store.
   * @returns {void} Nothing.
   */
  onChangeText = (value) => {
    this.setFormValue(value)
  }

  /**
   * Syncs the committed native text value.
   * @param {{nativeEvent?: {text?: string}}} event Native event carrying committed text.
   * @returns {void} Nothing.
   */
  onEndEditing = (event) => {
    this.setFormValue(event.nativeEvent?.text || "")
  }

  /**
   * Syncs the submitted text and submits the surrounding form.
   * @param {{nativeEvent?: {text?: string}}} event Native event carrying submitted text.
   * @returns {void} Nothing.
   * @throws {Error} When no surrounding Api Maker Form is registered.
   */
  onSubmitEditing = (event) => {
    if (this.p.disabled) {
      // Disabled fields mirror disabled submit controls and ignore keyboard submits.
      return
    }

    if (!this.form) throw new Error("FormTextInput requires an Api Maker Form for submitOnEnter")

    const submittedText = event.nativeEvent?.text
    this.form.setValue(
      this.props.name,
      submittedText !== undefined ? submittedText : this.form.getValue(this.props.name) || ""
    )
    this.form.submit()
  }
}

/** @type {NamedExoticComponent<FormTextInputProps>} */
const FormTextInputComponent = memo(shapeComponent(FormTextInput))

export default FormTextInputComponent
